import type { CompanyListItem } from '@tandemone/types'
import { useQuery } from '@tanstack/react-query'
import { Send } from 'lucide-react'
import { useNavigate } from 'react-router'

import { Button } from '@/components/ui/button'
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  useComboboxAnchor
} from '@/components/ui/combobox'
import { RichTextEditor } from '@/components/ui/rich-text-editor'

import { listCompaniesForPicker } from './api'
import { AttachmentPicker } from './attachment-picker'
import { useNewConversation } from './use-new-conversation'

/** compose form for /messages/new. state lives in NewConversationContext
 * (reducer + handleSubmit). this component is just the rendered surface
 * over that state.
 *
 * on success: provider navigates to /messages/<id>. the socket broadcast
 * also pushes the new row into other participants' caches.
 */
export function NewConversationForm() {
  const navigate = useNavigate()
  const anchor = useComboboxAnchor()
  const { state, dispatch, isValid, handleSubmit } = useNewConversation()

  const companiesQuery = useQuery({
    queryKey: ['companies', 'picker'],
    queryFn: ({ signal }) => listCompaniesForPicker(signal),
    staleTime: 60_000
  })
  const companies = companiesQuery.data?.items ?? []

  return (
    <form
      onSubmit={handleSubmit}
      className='flex w-full flex-col gap-5 rounded-xl border border-border bg-card p-4 sm:p-6'
    >
      <p className='text-[13px] text-ink-soft'>
        Pick one or more companies and write your first message.
      </p>

      <label className='flex flex-col gap-1.5'>
        <span className='text-[12px] font-medium text-ink'>Subject</span>
        <input
          type='text'
          value={state.subject}
          onChange={event =>
            dispatch({ type: 'SET_SUBJECT', payload: event.target.value })
          }
          maxLength={200}
          placeholder='Onboarding'
          /* not the Input primitive, so it carries its own ios-zoom guard */
          className='rounded-md border border-border bg-page px-3 py-2.5 text-base text-ink outline-none focus:border-accent sm:py-2 sm:text-[14px]'
        />
      </label>

      <div className='flex flex-col gap-1.5'>
        <span className='text-[12px] font-medium text-ink'>Recipients</span>
        {companiesQuery.isError ? (
          <p className='text-[12px] text-rose-700'>
            Failed to load companies. Try refreshing.
          </p>
        ) : (
          <Combobox
            multiple
            items={companies}
            value={state.recipients}
            onValueChange={(next: CompanyListItem[]) =>
              dispatch({ type: 'SET_RECIPIENTS', payload: next })
            }
            itemToStringLabel={(item: CompanyListItem) => item.name}
            itemToStringValue={(item: CompanyListItem) => item.name}
          >
            <ComboboxChips ref={anchor}>
              {state.recipients.map(company => (
                <ComboboxChip key={company.id}>{company.name}</ComboboxChip>
              ))}
              <ComboboxChipsInput
                placeholder={
                  companiesQuery.isLoading
                    ? 'Loading companies…'
                    : state.recipients.length === 0
                      ? 'Pick companies…'
                      : ''
                }
              />
            </ComboboxChips>
            <ComboboxContent anchor={anchor}>
              <ComboboxEmpty>No companies match.</ComboboxEmpty>
              <ComboboxList>
                {(item: CompanyListItem) => (
                  <ComboboxItem key={item.id} value={item}>
                    <span className='flex w-full items-center justify-between gap-2'>
                      <span>{item.name}</span>
                      <span className='text-[10px] text-ink-muted'>
                        {tagsFor(item)}
                      </span>
                    </span>
                  </ComboboxItem>
                )}
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
        )}
      </div>

      <div className='flex flex-col gap-1.5'>
        <span className='text-[12px] font-medium text-ink'>First message</span>
        <RichTextEditor
          value={state.body}
          onChange={next => dispatch({ type: 'SET_BODY', payload: next })}
          placeholder='Hi! Can you upload your insurance cert when you have a moment?'
          contentClassName='min-h-32'
        />
        <AttachmentPicker
          attachments={state.attachments}
          onChange={next =>
            dispatch({ type: 'SET_ATTACHMENTS', payload: next })
          }
          disabled={state.submitting}
        />
      </div>

      {state.error ? (
        <div className='rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-300'>
          {state.error}
        </div>
      ) : null}

      <div className='flex flex-col-reverse gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between'>
        <Button
          type='button'
          variant='ghost'
          size='lg'
          className='w-full sm:w-auto'
          onClick={() => navigate('/messages')}
          disabled={state.submitting}
        >
          Cancel
        </Button>
        <Button
          type='submit'
          size='hero'
          className='w-full sm:w-auto'
          disabled={state.submitting || !isValid}
        >
          <Send className='size-4' />
          {state.submitting ? 'Starting…' : 'Start conversation'}
        </Button>
      </div>
    </form>
  )
}

function tagsFor(item: CompanyListItem): string {
  const tags: string[] = []
  if (item.isSupplier)
    tags.push(item.isFoundingSupplier ? 'Founding' : 'Supplier')
  if (item.isBuyer) tags.push('Buyer')
  return tags.join(' · ')
}
